import React, { useState, useEffect, useCallback } from 'react';
import { FiPlus, FiSun, FiCheck, FiX } from 'react-icons/fi';
import Modal from '../components/ui/Modal';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const ESTADO_BADGE = { pendiente: 'badge-warning', aprobada: 'badge-ok', rechazada: 'badge-danger', cancelada: 'badge' };

export default function Vacaciones() {
  const { usuario } = useAuth();
  const esAdmin = usuario?.rol === 'admin' || usuario?.rol === 'superadmin';
  const [balance, setBalance] = useState(null);
  const [mias, setMias] = useState([]);
  const [todas, setTodas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [modal, setModal] = useState(false);
  const [diasSeleccionados, setDiasSeleccionados] = useState([]); // array de 'YYYY-MM-DD'
  const [motivo, setMotivo] = useState('');
  const [mesVisible, setMesVisible] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await api.get('/vacaciones/mis');
      setBalance(data.balance);
      setMias(data.solicitudes);
      if (esAdmin) {
        const res = await api.get('/vacaciones');
        setTodas(res.data);
      }
    } catch { /* noop */ } finally { setCargando(false); }
  }, [esAdmin]);

  useEffect(() => { cargar(); }, [cargar]);

  const toISODate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dia = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dia}`;
  };

  const toggleDia = (fechaISO) => {
    setDiasSeleccionados((prev) => (prev.includes(fechaISO) ? prev.filter((f) => f !== fechaISO) : [...prev, fechaISO].sort()));
  };

  const cambiarMes = (delta) => {
    setMesVisible((prev) => { const d = new Date(prev); d.setMonth(d.getMonth() + delta); return d; });
  };

  const diasDelMes = (fecha) => {
    const anio = fecha.getFullYear();
    const mes = fecha.getMonth();
    const primerDia = new Date(anio, mes, 1);
    const ultimoDia = new Date(anio, mes + 1, 0);
    const inicioSemana = (primerDia.getDay() + 6) % 7; // lunes = 0
    const celdas = [];
    for (let i = 0; i < inicioSemana; i += 1) celdas.push(null);
    for (let dia = 1; dia <= ultimoDia.getDate(); dia += 1) celdas.push(new Date(anio, mes, dia));
    return celdas;
  };

  const onCrear = async (e) => {
    e.preventDefault();
    if (diasSeleccionados.length === 0) { alert('Marca al menos un día en el calendario'); return; }
    setGuardando(true);
    try {
      await api.post('/vacaciones', { diasSeleccionados, motivo });
      setModal(false);
      setDiasSeleccionados([]);
      setMotivo('');
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al solicitar');
    } finally { setGuardando(false); }
  };

  const resolver = async (id, estado) => {
    try { await api.patch(`/vacaciones/${id}/resolver`, { estado }); cargar(); }
    catch (err) { alert(err.response?.data?.error || 'Error'); }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta solicitud?')) return;
    try { await api.delete(`/vacaciones/${id}`); cargar(); } catch { alert('No se pudo eliminar'); }
  };

  const cancelar = async (id) => {
    if (!window.confirm('¿Cancelar esta solicitud? Se restaurarán los días a tu balance.')) return;
    try { await api.patch(`/vacaciones/${id}/cancelar`); cargar(); }
    catch (err) { alert(err.response?.data?.error || 'No se pudo cancelar'); }
  };

  const fmt = (d) => new Date(d).toLocaleDateString('es-ES');
  const fmtCorto = (d) => new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
  const renderPeriodo = (solicitud) => {
    const fechas = Array.isArray(solicitud?.diasSeleccionados) ? solicitud.diasSeleccionados : [];
    const ordenadas = fechas
      .map((f) => new Date(f))
      .filter((d) => d instanceof Date && !Number.isNaN(d.getTime()))
      .sort((a, b) => a - b);

    if (ordenadas.length <= 1) {
      return <span>{fmt(solicitud?.fechaInicio || solicitud?.fechaFin)}</span>;
    }

    const consecutivas = ordenadas.every((fecha, index) => index === 0 || (fecha - ordenadas[index - 1]) === 86400000);
    if (consecutivas) {
      return <span>{`${fmtCorto(ordenadas[0])} – ${fmtCorto(ordenadas[ordenadas.length - 1])}`}</span>;
    }

    const vista = ordenadas.slice(0, 4).map((fecha) => fmtCorto(fecha));
    const extra = ordenadas.length > 4 ? ` +${ordenadas.length - 4}` : '';
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {vista.map((texto) => (
          <span key={texto} style={{ display: 'inline-flex', alignItems: 'center', borderRadius: 999, border: '1px solid var(--border)', padding: '2px 7px', fontSize: 12, whiteSpace: 'nowrap' }}>
            {texto}
          </span>
        ))}
        {extra ? <span style={{ fontSize: 12, color: 'var(--text-soft)' }}>{extra}</span> : null}
      </div>
    );
  };
  const nombre = (u) => (typeof u === 'object' && u ? `${u.nombre} ${u.apellidos || ''}`.trim() : '—');

  return (
    <div className="page">
      <div className="page-head">
        <div><h1 className="page-title">Vacaciones</h1><p className="page-subtitle">Solicita y consulta tu balance de días</p></div>
        <button className="btn btn-primary" onClick={() => setModal(true)}><FiPlus /> Solicitar vacaciones</button>
      </div>

      {balance && (
        <div className="kpi-grid">
          <div className="kpi-card"><span className="kpi-label">Días anuales</span><span className="kpi-value">{balance.diasAnuales}</span></div>
          <div className="kpi-card"><span className="kpi-label">Días disfrutados</span><span className="kpi-value">{balance.diasDisfrutados}</span></div>
          <div className="kpi-card"><span className="kpi-label">Días disponibles</span><span className="kpi-value">{balance.disponibles}</span></div>
        </div>
      )}

      <h3 className="permisos-titulo"><FiSun /> Mis solicitudes</h3>
      {cargando ? <p className="empty">Cargando…</p> : mias.length === 0 ? <p className="empty">No tienes solicitudes.</p> : (
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>Periodo</th><th>Días</th><th>Motivo</th><th>Estado</th><th></th></tr></thead>
            <tbody>
              {mias.map((s) => (
                <tr key={s._id}>
                  <td data-label="Periodo">{renderPeriodo(s)}</td>
                  <td data-label="Días">{s.dias}</td><td data-label="Motivo">{s.motivo || '—'}</td>
                  <td data-label="Estado"><span className={`badge ${ESTADO_BADGE[s.estado]}`}>{s.estado}</span></td>
                  <td className="acciones">
                    {(s.estado === 'pendiente' || s.estado === 'aprobada') && (
                      <button className="icon-btn danger" onClick={() => cancelar(s._id)} title="Cancelar">
                        <FiX /> Cancelar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {esAdmin && (
        <>
          <h3 className="permisos-titulo"><FiSun /> Solicitudes del equipo</h3>
          {todas.length === 0 ? <p className="empty">No hay solicitudes del equipo.</p> : (
            <div className="table-wrap">
              <table className="data-table">
                <thead><tr><th>Empleado</th><th>Periodo</th><th>Días</th><th>Estado</th><th></th></tr></thead>
                <tbody>
                  {todas.map((s) => (
                    <tr key={s._id}>
                      <td data-label="Empleado">{nombre(s.usuarioId)}</td>
                      <td data-label="Periodo">{renderPeriodo(s)}</td>
                      <td data-label="Días">{s.dias}</td>
                      <td data-label="Estado"><span className={`badge ${ESTADO_BADGE[s.estado]}`}>{s.estado}</span></td>
                      <td className="acciones">
                        {s.estado === 'pendiente' && <>
                          <button className="icon-btn ok" onClick={() => resolver(s._id, 'aprobada')} title="Aprobar"><FiCheck /></button>
                          <button className="icon-btn danger" onClick={() => resolver(s._id, 'rechazada')} title="Rechazar"><FiX /></button>
                        </>}
                        {s.estado !== 'cancelada' && <button className="icon-btn danger" onClick={() => cancelar(s._id)} title="Cancelar"><FiX /></button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <Modal abierto={modal} onCerrar={() => setModal(false)} titulo="Solicitar vacaciones" ancho={480}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <button type="button" className="btn" onClick={() => cambiarMes(-1)}>‹</button>
            <strong>{mesVisible.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}</strong>
            <button type="button" className="btn" onClick={() => cambiarMes(1)}>›</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, textAlign: 'center' }}>
            {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((d) => <span key={d} style={{ fontSize: 12, color: 'var(--text-soft)', fontWeight: 600 }}>{d}</span>)}
            {diasDelMes(mesVisible).map((fecha, i) => {
              if (!fecha) return <span key={`vacio-${i}`} />;
              const iso = toISODate(fecha);
              const seleccionado = diasSeleccionados.includes(iso);
              return (
                <button
                  type="button"
                  key={iso}
                  onClick={() => toggleDia(iso)}
                  style={{
                    padding: '8px 0',
                    borderRadius: 8,
                    border: seleccionado ? '2px solid var(--primary, #4f46e5)' : '1px solid var(--border)',
                    background: seleccionado ? 'var(--primary, #4f46e5)' : 'transparent',
                    color: seleccionado ? '#fff' : 'inherit',
                    cursor: 'pointer',
                    fontWeight: seleccionado ? 700 : 400,
                  }}
                >
                  {fecha.getDate()}
                </button>
              );
            })}
          </div>
          <p style={{ margin: 0, fontWeight: 600 }}>{diasSeleccionados.length} día(s) seleccionado(s)</p>
          <label className="field"><span>Motivo</span><input value={motivo} onChange={(e) => setMotivo(e.target.value)} /></label>
          <div className="form-actions">
            <button type="button" className="btn" onClick={() => setModal(false)}>Cancelar</button>
            <button type="button" className="btn btn-primary" onClick={onCrear} disabled={guardando || diasSeleccionados.length === 0}>{guardando ? 'Enviando…' : 'Solicitar'}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
